import { HeartIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Spinner } from '@/components/common';
import { isLikeablePhoto } from '@/services';
import type { ProfilePhoto } from '@/types/models';

interface PhotoLikeButtonProps {
  photo: ProfilePhoto;
  liked: boolean;
  pending?: boolean;
  onToggle: () => void;
}

/**
 * Overlay like toggle for a photo on another member's profile. Stops the click
 * from bubbling so the enclosing carousel swipe does not fire.
 */
export function PhotoLikeButton({ photo, liked, pending, onToggle }: PhotoLikeButtonProps) {
  const { t } = useTranslation('profile');

  if (!isLikeablePhoto(photo)) return null;

  return (
    <Box position="absolute" bottom="3" right="3" className="z-10">
      <IconButton
        type="button"
        radius="full"
        variant="soft"
        size="1"
        color={liked ? 'red' : 'gray'}
        highContrast={!liked}
        disabled={pending}
        className="transition-transform active:scale-90"
        data-testid={`like-photo-${photo.id}`}
        aria-label={t(liked ? 'view.actions.unlikePhoto' : 'view.actions.likePhoto')}
        aria-pressed={liked}
        aria-busy={pending}
        onClick={event => {
          event.stopPropagation();
          onToggle();
        }}
      >
        {pending ? (
          <Spinner size="1" />
        ) : (
          <HeartIcon size={20} fill={liked ? 'currentColor' : 'none'} />
        )}
      </IconButton>
    </Box>
  );
}
